import dayjs from 'dayjs';

/**
 * Placeholders que o backend usa como "conteúdo" de mídia sem legenda real
 * (ex.: "[imagem]", "[áudio]"). Servem para o preview da lista de conversas,
 * mas NÃO devem ser exibidos como legenda dentro do balão de mídia.
 */
const PLACEHOLDER_CAPTIONS = new Set([
  '[imagem]', '[image]',
  '[áudio]', '[audio]',
  '[vídeo]', '[video]',
  '[documento]', '[document]',
  '[figurinha]', '[sticker]',
  '[mídia]', '[midia]', '[media]',
  '[sem texto]',
]);

/** Retorna a legenda real da mensagem, ou null quando for apenas um placeholder de mídia. */
export function getDisplayCaption(content?: string | null): string | null {
  const trimmed = (content ?? '').trim();
  if (!trimmed) return null;
  if (PLACEHOLDER_CAPTIONS.has(trimmed.toLowerCase())) return null;
  return content ?? null;
}

/** URL usável para imagem/vídeo/áudio (API camelCase, snake_case ou anexo). */
export function coalesceMediaUrl(
  m: { mediaUrl?: string | null; attachment?: { url?: string } } & Record<string, unknown>
): string | null {
  const raw = m as Record<string, unknown>;
  const candidates = [m.mediaUrl, raw.media_url, raw.MediaUrl, m.attachment?.url];
  for (const c of candidates) {
    if (c != null && String(c).trim() !== '') {
      return String(c).trim();
    }
  }
  return null;
}

/**
 * Quando o servidor ainda devolve áudio sem URL (upload/async), mantém o blob: do envio otimista
 * para o player continuar a funcionar até existir URL definitiva.
 */
export function enrichAudioMessageWithPendingBlob<
  T extends {
    mediaType?: string;
    mediaUrl?: string | null;
    isSent?: boolean;
    sentAt?: string;
    status?: string;
  },
>(fm: T, prev: T[]): T {
  const mt = (fm.mediaType || '').toLowerCase();
  if (mt !== 'audio' && mt !== 'voice') return fm;
  if (String(fm.mediaUrl || '').trim()) return fm;

  const candidates = prev.filter(
    m =>
      m.status === 'sending' &&
      ['audio', 'voice'].includes((m.mediaType || '').toLowerCase()) &&
      m.isSent &&
      fm.isSent
  );
  const match = candidates.find(m => {
    const timeDiff = Math.abs(dayjs(m.sentAt).valueOf() - dayjs(fm.sentAt).valueOf());
    return timeDiff <= 20000;
  });
  const blob = match?.mediaUrl;
  if (blob && String(blob).startsWith('blob:')) {
    return { ...fm, mediaUrl: blob };
  }
  return fm;
}
